import React from 'react';
import classes from './Table.module.css';
import { ITable, ITableColumn } from './Table.types';
import { TableHeader } from './TableHeader/TableHeader';
import { TableRow } from './TableRow/TableRow';
import { TableCell } from './TableCell/TableCell';
import { Text } from '../Text';
import { ChevronLeftIcon, ChevronRightIcon } from '../../assets/icons';
import { Spinner } from '../../assets/icons/Spinner';

export const Table = <T extends Record<string, any>>(props: ITable<T>) => {
    const [selectedRow, setSelectedRow] = React.useState<string | null>(null);
    const { columns, data, keyExtractor, emptyMessage = 'No data available', onRowClick, Pagination, loading } = props;

    const getDefaultKey = (row: T, index: number): string => {
        return row.id || row.key || `row-${index}`;
    };

    const getCellValue = (row: T, column: ITableColumn<T>): any => {
        const keys = column.key.split('.');
        let value: any = row;
        for (const key of keys) {
            value = value?.[key];
            if (value === undefined) break;
        }
        return value;
    };

    const onClickRow = (row: T, index: number) => {
        // deselect row
        if (selectedRow === getDefaultKey(row, index)) {
            setSelectedRow(null);
            onRowClick && onRowClick(row, index);
            return;
        }

        setSelectedRow(getDefaultKey(row, index));
        if (onRowClick) {
            onRowClick(row, index);
        }
    };

    return (
        <div className={classes.tableWrapper}>
            <table className={classes.table}>
                <thead className={classes.tableHeader}>
                    <TableRow isHeader>
                        {columns.map((column: ITableColumn<T>, index: number) => (
                            <TableHeader
                                key={`${column.key}-${index}`}
                                label={column.label}
                                width={column.width}
                                minWidth={column.minWidth}
                                align={column.align}
                            />
                        ))}
                    </TableRow>
                </thead>
                <tbody className={`${classes.tableBody} ${data.length > 9 ? classes.scrollable : ''}`}>
                    {loading && (
                        <tr>
                            <td colSpan={columns.length} className={classes.emptyMessage}>
                                <Spinner className={classes.loader} />
                            </td>
                        </tr>
                    )}
                    {data.length === 0 && !loading && (
                        <tr>
                            <td colSpan={columns.length} className={classes.emptyMessage}>
                                {emptyMessage}
                            </td>
                        </tr>
                    )}

                    {data.length > 0 &&
                        !loading &&
                        data.map((row: T, rowIndex: number) => {
                            const rowKey = keyExtractor ? keyExtractor(row, rowIndex) : getDefaultKey(row, rowIndex);
                            return (
                                <TableRow
                                    key={rowKey}
                                    onClick={onRowClick ? () => onClickRow(row, rowIndex) : undefined}
                                    className={onRowClick ? classes.clickableRow : undefined}
                                >
                                    {columns.map((column: ITableColumn<T>, colIndex: number) => {
                                        const cellValue = getCellValue(row, column);
                                        const displayValue = column.render
                                            ? column.render(cellValue, row, rowIndex)
                                            : cellValue;

                                        const isRowSelected = selectedRow === rowKey;

                                        return (
                                            <TableCell
                                                showMarker={isRowSelected && colIndex === 0}
                                                isSelected={isRowSelected}
                                                key={`${rowKey}-${column.key}-${colIndex}`}
                                                align={column.align}
                                                width={column.width}
                                                minWidth={column.minWidth}
                                            >
                                                <div className={classes.tableCellWrapper}>
                                                    {column.showStatus && (
                                                        <div
                                                            className={`${classes.status} ${cellValue.toLowerCase() === 'live' ? classes.live : classes.draft}`}
                                                        />
                                                    )}

                                                    {displayValue}
                                                </div>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                </tbody>
                <tfoot className={classes.tableFooter}>
                    {Pagination && (
                        <tr className={classes.paginationRow}>
                            <td colSpan={columns.length}>
                                <div style={{
                                    display: 'flex',
                                    width: '100%',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}>
                                    <div>
                                        <select
                                            value={Pagination.itemsPerPage}
                                            onChange={(e) => Pagination.onChangeItemsPerPage(Number(e.target.value))}
                                            className={classes.select}
                                        >
                                            <option value={1}>1</option>
                                            <option value={9}>9</option>
                                            <option value={20}>20</option>
                                            <option value={30}>30</option>
                                        </select>

                                        <span
                                            style={{
                                                marginLeft: '8px',
                                            }}
                                        >
                                            <Text variant="body" text="Per Page" />
                                        </span>

                                        <span
                                            style={{
                                                marginLeft: '40px',
                                            }}
                                        >
                                            <Text
                                                variant="body"
                                                text={
                                                    Pagination.totalNumberOfRows > 0 ? `1-${Pagination.totalNumberOfRows < Pagination.itemsPerPage ? Pagination.totalNumberOfRows : Pagination.itemsPerPage} of ${Pagination.totalNumberOfRows}` : ''
                                                }
                                            />
                                        </span>
                                    </div>
                                    <div className={classes.paginationControls}>
                                        {Pagination.pageNumbers.map((page) => (
                                            <button
                                                onClick={() => Pagination.onSelectPage(Number(page))}
                                                key={page}
                                                className={`${classes.pageButton} ${Pagination.currentPage === page ? classes.active : ''}`}
                                            >
                                                {page}
                                            </button>
                                        ))}
                                        <div className={classes.controls}>
                                            <button
                                                onClick={Pagination.onPrevPage}
                                                disabled={Pagination.currentPage === 1 || Pagination.totalPages === 0}
                                                className={classes.prevPage}
                                            >
                                                <ChevronLeftIcon />
                                            </button>
                                            <button
                                                disabled={Pagination.currentPage === Pagination.totalPages || Pagination.totalPages === 0}
                                                onClick={Pagination.onNextPage}
                                                className={classes.nextPage}
                                            >
                                                <ChevronRightIcon />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    )}
                </tfoot>
            </table>
        </div>
    );
};
